import { supabase } from '../supabase';
import type { Asker, TaskStatus, Tasks } from '../utils/types';

export const fetchAskerProfile = async (userId: string) => {
  if (!userId) {
    console.error('No user ID provided to fetchAskerProfile');
    return null;
  }
  try {
    const { data: userData, error: userError } = await supabase
      .from('askers')
      .select('*')
      .eq('auth_id', userId)
      .single();

    if (userError) throw userError;
    return userData;
  } catch (error) {
    console.error('Error fetching user asker:', error);
    throw new Error('Failed to fetch user asker');
  }
};

export const fetchAskerTasks = async (askerId: number) => {
  if (!askerId) {
    console.error('No user ID provided to fetchAskerProfile');
    return null;
  }
  try {
    const { data: tasks, error: userError } = await supabase
      .from('tasks')
      .select('*')
      .eq('asker_id', askerId)
      .order('created_at', { ascending: false });

    if (userError) throw userError;
    return tasks as Tasks[];
  } catch (error) {
    console.error('Error fetching asker tasks:', error);
    throw new Error('Failed to fetch asker tasks');
  }
};

export const fetchAskerTasksByStatus = async (
  askerId: number,
  status: TaskStatus,
  { onlyAssigned }: { onlyAssigned: boolean }
) => {
  if (!askerId) {
    console.error('No user ID provided to fetchAskerTasksByStatus');
    return null;
  }
  try {
    const query = supabase
      .from('tasks')
      .select(
        `
        id,
        category:categories (
          id,
          name
        ),
        description,
        date,
        is_asap,
        is_flexible_date,
        status,
        task_offers (
          id,
          tasker:taskers (
            id,
            name,
            last_name,
            photo_selfie_url,
            university
          )
        )
      `
      )
      .eq('asker_id', askerId)
      .eq('status', status);

    if (status === 'assigned' && onlyAssigned) {
      query.eq('task_offers.status', 'assigned');
    }

    const { data: tasks, error: tasksError } = await query;

    if (tasksError) throw tasksError;

    return tasks as unknown as []
  } catch (error) {
    console.error('Error fetching asker tasks by status:', error);
    throw new Error('Failed to fetch asker tasks by status');
  }
};

export const fetchAskerAddresses = async (askerId: number) => {
  if (!askerId) {
    console.error('No asker ID provided to fetchAskerAddresses');
    return null;
  }
  try {
    const { data: addresses, error: addressesError } = await supabase
      .from('askers_addresses')
      .select('*')
      .eq('asker_id', askerId)
      .order('created_at', { ascending: false });

    if (addressesError) throw addressesError;

    if (!addresses || addresses.length === 0) {
      return null;
    }

    return addresses;
  } catch (error) {
    console.error('Error fetching asker addresses:', error);
    throw new Error('Failed to fetch asker addresses');
  }
};

export const createAskerAddress = async ({
  address,
  commune,
  askerId,
  city,
  region,
}: {
  address: string;
  commune: string;
  askerId?: number;
  city?: string;
  region?: string;
}) => {
  try {
    const insertData: {
      address: string;
      commune: string;
      city?: string;
      asker_id?: number;
      region?: string;
    } = { address, commune, city, region };
    if (askerId !== undefined) {
      insertData.asker_id = askerId;
    }

    const { data, error } = await supabase
      .from('askers_addresses')
      .insert([insertData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating asker address:', error);
    throw new Error('Failed to create asker address');
  }
};

export const fetchAskerAddressByTask = async (taskId: number) => {
  if (!taskId) {
    console.error('taskId must be provided to fetchAskerAddressByTask');
    return null;
  }
  try {
    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .select('askers_addresses_id')
      .eq('id', taskId)
      .single();

    if (taskError) throw taskError;

    if (!task.askers_addresses_id) {
      return null;
    }

    const { data: address, error: addressError } = await supabase
      .from('askers_addresses')
      .select('*')
      .eq('id', task.askers_addresses_id)
      .single();

    if (addressError) throw addressError;
    return address;
  } catch (error) {
    console.error('Error fetching asker address by task:', error);
    throw new Error('Failed to fetch asker address by task');
  }
};

export const createAskerProfile = async ({
  auth_id,
  name,
  last_name,
  email,
  phone_number,
  rut,
}: {
  auth_id?: string;
  name?: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  rut?: string;
}) => {
  try {
    const { data, error } = await supabase
      .from('askers')
      .insert([{ auth_id, name, last_name, email, phone_number, rut }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating asker profile:', error);
    throw new Error('Failed to create asker profile');
  }
};

export const updateAsker = async (
  askerId: number,
  updates: Partial<Asker>
): Promise<Asker | null> => {
  try {
    const { data, error } = await supabase
      .from('askers')
      .update(updates)
      .eq('id', askerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating asker profile:', error);
    throw new Error('Failed to update asker profile');
  }
};

export const associateAskerWithAuthUser = async (
  askerId: number,
  authUserId: string
) => {
  const { data, error } = await supabase
    .from('askers')
    .update({ auth_id: authUserId })
    .eq('id', askerId);

  if (error) {
    throw error;
  }

  return data;
};

export const fetchAskerByEmail = async (
  email: string
): Promise<Asker | null> => {
  try {
    const { data, error } = await supabase
      .from('askers')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No asker found with this email
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching asker by email:', error);
    throw new Error('Failed to fetch asker by email');
  }
};

export const getAskerById = async (askerId: number): Promise<Asker | null> => {
  try {
    const { data, error } = await supabase
      .from('askers')
      .select('*')
      .eq('id', askerId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No asker found with this ID
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching asker by ID:', error);
    throw new Error('Failed to fetch asker by ID');
  }
};

export const fetchAllAsker = () => {
  return supabase.from('askers').select('*');
}